import { Alert, Grid, Snackbar } from "@mui/material";
import { LoadingScreen } from "components/LoadingScreen";
import { Placeholder } from "components/Placeholder";
import {
  Claim,
  ServiceSummary,
  UpdateUserRequest,
  User,
  UserWithClaims,
} from "data/interfaces";
import ServicePane from "components/ServicePane";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ApiService, Error } from "services";
import RolesView from "./RolesView";
import { ErrorHandler } from "components/ErrorHandler";

const RolesPage = () => {
  const { accountId } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error>();
  const [notifyMessage, setNotifyMessage] = useState<string>();

  const [services, setServices] = useState<ServiceSummary[]>([]);
  const [selectedService, setSelectedService] = useState<ServiceSummary | null>(
    null
  );

  const [users, setUsers] = useState<User[]>([]);

  useEffect(() => {
    accountId &&
      ApiService.getServices(accountId)
        .then((services: ServiceSummary[]) => {
          ApiService.getUsers(accountId)
            .then((users: User[]) => {
              setServices(services);
              setUsers(users);
              setLoading(false);
            })
            .catch((error: Error) => setError(error));
        })
        .catch((error: Error) => setError(error));
  }, [accountId]);

  const handleAssignUsers = (
    assignUserRequests: UserWithClaims[],
    service: ServiceSummary
  ) => {
    const updateClaimsRequests: UpdateUserRequest[] = assignUserRequests.map(
      (r) => {
        return {
          id: r.user.id,
          email: r.user.email,
          admin: r.user.isAdmin,
          serviceClaims: [
            {
              serviceReference: service.serviceReference,
              licenseReference: service.licenseReference,
              claims: r.claims,
            },
          ],
        };
      }
    );

    accountId &&
      ApiService.updateClaimsFor(accountId, updateClaimsRequests)
        .then((updatedServices: ServiceSummary[]) => {
          ApiService.getUsers(accountId)
            .then((users: User[]) => {
              setUsers(users);
            })
            .catch((error: Error) => setError(error));
          setSelectedService(
            updatedServices.find(
              (s) =>
                s.serviceReference === service.serviceReference &&
                s.licenseReference === service.licenseReference
            ) ?? null
          );
          setServices(updatedServices);
        })
        .catch((error: Error) =>
          setNotifyMessage("Failed to add roles for the user.")
        );
  };

  const handleUpdateRolesForUsers = (
    usersToUpdate: User[],
    service: ServiceSummary,
    claims: Claim[]
  ) => {
    const updateClaimsRequests: UpdateUserRequest[] = usersToUpdate.map((u) => {
      return {
        id: u.id,
        email: u.email,
        admin: u.isAdmin,
        serviceClaims: [
          {
            serviceReference: service.serviceReference,
            licenseReference: service.licenseReference,
            claims: claims,
          },
        ],
      };
    });

    accountId &&
      ApiService.updateClaimsFor(accountId, updateClaimsRequests)
        .then((updatedServices: ServiceSummary[]) => {
          setUsers(
            users.map(
              (user) =>
                usersToUpdate.find((u) =>
                  u.id !== null ? u.id === user.id : u.email === user.email
                ) ?? user
            )
          );

          setSelectedService(
            updatedServices.find(
              (s) =>
                s.serviceReference === service.serviceReference &&
                s.licenseReference === service.licenseReference
            ) ?? null
          );
          setServices(updatedServices);
        })
        .catch((error: Error) =>
          setNotifyMessage("Failed to add roles for the user.")
        );
  };

  const handleSelectService = (service: ServiceSummary) => {
    setSelectedService(service);
  };

  return (
    <ErrorHandler error={error}>
      {loading && <LoadingScreen title="Loading..." />}
      
      {!loading && services.length === 0 && (
        <Placeholder>No services found</Placeholder>
      )}

      {!loading && services.length > 0 && (
        <Grid container sx={{ minWidth: 850 }}>
          <Grid item xs={3}>
            <ServicePane
              services={services}
              selected={selectedService}
              onSelect={handleSelectService}
              users={users}
            />
          </Grid>
          <Grid item xs={9}>
            {selectedService ? (
              <RolesView
                selectedService={selectedService}
                users={users}
                onUpdateRolesForUsers={handleUpdateRolesForUsers}
                onAssignUsers={handleAssignUsers}
              />
            ) : (
              <Placeholder>No service selected</Placeholder>
            )}
          </Grid>
        </Grid>
      )}

      <Snackbar
        open={notifyMessage !== undefined}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        autoHideDuration={8000}
        onClose={() => setNotifyMessage(undefined)}
      >
        <Alert onClose={() => setNotifyMessage(undefined)} severity="error">
          {notifyMessage}
        </Alert>
      </Snackbar>
    </ErrorHandler>
  );
};

export default RolesPage;
